import { type ChangeEvent, type FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, FileUp, PackagePlus } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import { SearchSelect, WorkspacePage } from '@forq/ui';
import type { CompanyProfileGateway } from '@forq/platform-data';
import type { MachinesGateway, MachineTypeRecord } from '@forq/machines-data';
import type { MasterdataGateway } from '@forq/masterdata-data';
import { FinanceNav } from './FinanceNav';
import { extractPdfText } from './ankauf/extractPdfText';
import { recognizeAcquisition, type RecognizedAcquisition } from './ankauf/recognizeAcquisition';

interface AnkaufPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly machinesGateway: MachinesGateway;
  readonly masterdataGateway: MasterdataGateway;
  readonly companyGateway: CompanyProfileGateway;
}

const DRIVE_OPTIONS = ['Elektro', 'Diesel', 'Treibgas', 'Benzin', 'Hybrid', 'Lithium-Ionen'];
const CONDITION_OPTIONS = ['neu', 'gebraucht', 'generalüberholt', 'defekt'];

/** Erkannte techn. Werte → Maschinen-attributes.*; werden beim Anlegen auf die Feld-Keys des
 *  gewählten Typs gefiltert (unbekannte Keys würden createMachine ablehnen). */
const ATTRIBUTE_FIELDS = [
  { key: 'capacity', label: 'Tragkraft (kg)', kind: 'number' as const },
  { key: 'drive', label: 'Antrieb', kind: 'select' as const, options: DRIVE_OPTIONS },
  { key: 'hours', label: 'Betriebsstunden', kind: 'number' as const },
  { key: 'liftHeight', label: 'Hubhöhe (mm)', kind: 'number' as const },
  { key: 'zustand', label: 'Zustand', kind: 'select' as const, options: CONDITION_OPTIONS },
];

function errorMessage(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Der Vorgang konnte nicht verarbeitet werden.';
}

function parseAmount(raw: string): number | undefined {
  const value = Number.parseFloat(raw.replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

export function AnkaufPage({ tenant, mode, permissions, machinesGateway, masterdataGateway, companyGateway }: AnkaufPageProps) {
  const navigate = useNavigate();
  const [types, setTypes] = useState<readonly MachineTypeRecord[]>([]);
  const [ownName, setOwnName] = useState('');
  const [recognized, setRecognized] = useState<RecognizedAcquisition | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ machineLabel: string; supplierName: string; created: boolean } | null>(null);

  const canCreate = permissions.has('machines.create') && permissions.has('masterdata.suppliers.manage');

  useEffect(() => { void machinesGateway.listMachineTypes(tenant.id).then(setTypes).catch(() => undefined); }, [machinesGateway, tenant.id]);
  useEffect(() => { void companyGateway.loadProfile(tenant.id).then((profile) => setOwnName(profile.identity.name)).catch(() => undefined); }, [companyGateway, tenant.id]);

  const defaultTypeId = types.find((type) => type.code === 'forklift')?.id ?? types[0]?.id ?? '';

  const handleFile = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true); setError(null); setDone(null);
    try {
      const text = await extractPdfText(file);
      setRecognized(recognizeAcquisition(text, ownName));
      setFormKey((key) => key + 1);
    } catch (readError) {
      setError(`Der Beleg konnte nicht gelesen werden – ist es ein digitales PDF? (${errorMessage(readError)})`);
    } finally {
      setBusy(false);
      event.target.value = '';
    }
  }, [ownName]);

  const submit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true); setError(null);
    try {
      const typeId = String(form.get('typeId') ?? '');
      const type = types.find((entry) => entry.id === typeId);
      const typeKeys = new Set(type?.fields.map((field) => field.key) ?? []);

      const supplierName = String(form.get('supplierName') ?? '').trim();
      const vatId = String(form.get('supplierVatId') ?? '').trim();
      let supplier = null;
      if (vatId || supplierName) {
        const page = await masterdataGateway.searchSuppliers({ tenantId: tenant.id, query: vatId || supplierName, pageSize: 10 });
        supplier = page.items.find((entry) =>
          (vatId && entry.vatId && entry.vatId.toUpperCase() === vatId.toUpperCase())
          || entry.name.toLowerCase() === supplierName.toLowerCase()) ?? null;
      }
      const supplierExisted = !!supplier;
      if (!supplier) {
        supplier = await masterdataGateway.saveSupplier({
          tenantId: tenant.id,
          name: supplierName,
          vatId: vatId || undefined,
          street: String(form.get('supplierStreet') ?? '').trim() || undefined,
          postalCode: String(form.get('supplierPostalCode') ?? '').trim() || undefined,
          city: String(form.get('supplierCity') ?? '').trim() || undefined,
        });
      }

      const attributes: Record<string, string | number> = {};
      for (const field of ATTRIBUTE_FIELDS) {
        if (!typeKeys.has(field.key)) continue;
        const raw = String(form.get(field.key) ?? '').trim();
        if (!raw) continue;
        attributes[field.key] = field.kind === 'number' ? Number(raw.replace(',', '.')) : raw;
      }
      const year = Number(String(form.get('constructionYear') ?? '').trim());
      const machine = await machinesGateway.createMachine({
        tenantId: tenant.id,
        typeId,
        manufacturer: String(form.get('manufacturer') ?? '').trim(),
        model: String(form.get('model') ?? '').trim(),
        serialNumber: String(form.get('serialNumber') ?? '').trim() || undefined,
        constructionYear: Number.isFinite(year) && year > 1900 ? year : null,
        purchasePrice: parseAmount(String(form.get('purchasePrice') ?? '')),
        status: 'stock',
        attributes,
      });

      setDone({
        machineLabel: `${machine.manufacturer} ${machine.model} · Nr. ${machine.number}`.trim(),
        supplierName: supplier.name,
        created: !supplierExisted,
      });
      setRecognized(null);
    } catch (createError) {
      setError(errorMessage(createError));
    } finally {
      setBusy(false);
    }
  }, [types, machinesGateway, masterdataGateway, tenant.id]);

  return (
    <WorkspacePage
      customizeKey="finance-ankauf"
      eyebrow={`FORQ Finance · Ankauf · ${mode === 'demo' ? 'Demo' : 'Supabase'}`}
      title="Ankauf – Maschine einlesen"
      description="Händlerbeleg (Auftragsbestätigung oder Rechnung als PDF) einlesen – FORQ erkennt Maschine und Lieferant, du prüfst und legst beides an."
      toolbar={<div className="finance-toolbar"><FinanceNav /></div>}
    >
      {error && <div className="finance-page__error">{error}</div>}

      {done ? (
        <section className="ankauf-done">
          <CheckCircle2 size={22} aria-hidden />
          <div>
            <strong>Angelegt.</strong>
            <p>Maschine <strong>{done.machineLabel}</strong> im Bestand · Lieferant <strong>{done.supplierName}</strong> {done.created ? 'neu angelegt' : '(bereits vorhanden, verknüpft)'}.</p>
            <div className="ankauf-done__actions">
              <button type="button" className="finance-actions__btn" onClick={() => navigate('/maschinen')}>Zum Maschinenstamm</button>
              <button type="button" className="finance-actions__btn" onClick={() => setDone(null)}>Weiteren Ankauf einlesen</button>
            </div>
          </div>
        </section>
      ) : (
        <label className={`ankauf-drop${busy ? ' is-busy' : ''}`}>
          <FileUp size={22} aria-hidden />
          <span>{busy ? 'Beleg wird gelesen …' : 'Händlerbeleg als PDF hier einlesen'}</span>
          <small>Auftragsbestätigung (mit Technikdaten) oder Rechnung. Digitale PDFs werden 100 % genau gelesen.</small>
          <input type="file" accept="application/pdf,.pdf" disabled={busy || !canCreate} onChange={handleFile} />
        </label>
      )}

      {!canCreate && (
        <p className="finance-note is-warning">Für den Ankauf brauchst du die Rechte zum Anlegen von Maschinen und Lieferanten.</p>
      )}

      {recognized && (
        <form key={formKey} className="ankauf-form" onSubmit={submit}>
          <div className="ankauf-cols">
            <fieldset>
              <legend><PackagePlus size={15} /> Maschine</legend>
              <label className="ankauf-wide">Maschinentyp
                <SearchSelect name="typeId" defaultValue={defaultTypeId} options={types.map((type) => ({ value: type.id, label: type.name }))} />
              </label>
              <div className="ankauf-grid">
                <label>Hersteller<input name="manufacturer" defaultValue={recognized.manufacturer} /></label>
                <label>Modell / Typ<input name="model" defaultValue={recognized.model} /></label>
                <label>Serien-Nr.<input name="serialNumber" defaultValue={recognized.serialNumber} /></label>
                <label>Baujahr<input name="constructionYear" type="number" defaultValue={recognized.constructionYear ?? ''} /></label>
                <label>Tragkraft (kg)<input name="capacity" type="number" defaultValue={recognized.capacity ?? ''} /></label>
                <label>Antrieb<SearchSelect name="drive" defaultValue={recognized.drive} placeholder="– wählen –" options={DRIVE_OPTIONS.map((value) => ({ value, label: value }))} /></label>
                <label>Betriebsstunden<input name="hours" type="number" defaultValue={recognized.hours ?? ''} /></label>
                <label>Hubhöhe (mm)<input name="liftHeight" type="number" defaultValue={recognized.liftHeight ?? ''} /></label>
                <label>Zustand<SearchSelect name="zustand" defaultValue={recognized.condition} placeholder="– wählen –" options={CONDITION_OPTIONS.map((value) => ({ value, label: value }))} /></label>
                <label>Kaufpreis netto (EUR)<input name="purchasePrice" defaultValue={recognized.purchasePrice ?? ''} /></label>
              </div>
            </fieldset>

            <fieldset>
              <legend><FileUp size={15} /> Lieferant</legend>
              <div className="ankauf-grid">
                <label className="ankauf-wide">Lieferant<input name="supplierName" required defaultValue={recognized.supplierName} /></label>
                <label>USt-IdNr.<input name="supplierVatId" defaultValue={recognized.supplierVatId} /></label>
                <label>Straße<input name="supplierStreet" defaultValue={recognized.supplierStreet} /></label>
                <label>PLZ<input name="supplierPostalCode" defaultValue={recognized.supplierPostalCode} /></label>
                <label>Ort<input name="supplierCity" defaultValue={recognized.supplierCity} /></label>
              </div>
              <p className="ankauf-hint">Ist der Lieferant über Name oder USt-IdNr. schon im Stamm, wird er verknüpft statt doppelt angelegt.</p>
            </fieldset>
          </div>

          <div className="ankauf-actions">
            <button type="button" className="finance-actions__btn" onClick={() => setRecognized(null)}>Verwerfen</button>
            <button type="submit" className="finance-actions__btn is-pay" disabled={busy}>Maschine &amp; Lieferant anlegen</button>
          </div>
        </form>
      )}
    </WorkspacePage>
  );
}
